import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  DEFAULT_RETRY,
  NETWORK_ERROR_RETRY,
  isNetworkErroFunc,
} from "../../../utils/calculateCartHepler";
import {
  buildCartLine,
  buildLoyaltyOrderLine,
} from "../../../utils/cart/cartLineFactory";
import {
  AddLineInput,
  RemoveLineInput,
  addLineInputToCartItemType,
  normalizeAddPayload,
} from "../../../utils/cart/cartLineTypes";
import {
  applyCartOfferResponseStatus,
  getOfferRemovalLineIds,
} from "../../../utils/cart/loyaltyOfferResponse";
import { addLineInputToOrderLine } from "../../../utils/cart/orderLineMappers";
import CartItem from "../../../utils/CartItem";
import { CUSTOMER_ANALYTICS_EVENT } from "../../../utils/enums";
import { SuccessReportTypes } from "../../../utils/ErrorTypes";
import { DoubleLinkedList } from "../../../utils/help/linkedList/LinkedList";
import {
  nextBogoStep,
  previousBogoStep,
} from "../../../utils/help/offers/offers";
import { LoyaltyItemOffer } from "../../../utils/LoyaltyOfferClass/LoyaltyItemOffer";
import { LoyaltyOfferType } from "../../../utils/loyaltyTypes";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { createOffer } from "../../../utils/OfferClass/OfferClassUtils/OfferCreator/offerCreator";
import {
  AnalyticsExtraInfo,
  OutOfStockItem,
  SubItemType,
} from "../../../utils/types";
import {
  calculateCartWithOffersCall,
  calculateOrderCartV2Call,
} from "../http/cartAndLoyaltyV2";
import {
  CartAction,
  CartActionType,
  CartItemType,
  CartLoyaltyV2SliceType,
  RedirectToItem,
} from "../types/CartLoyaltyV2Types";
import {
  LoyaltyOfferBuilderCustomisation,
  initialLoyaltyCartState,
} from "../types/LoyaltyCartTypes";
import {
  OrderCartV2Line,
  OrderCartV2OfferSelection,
} from "../types/OrderCartV2Types";
import { replaceNullWithUndefined } from "../types/TypeHelper";
import { clearLoyaltyRedemption } from "../utils/loyaltyCartHelpers";

function clearRedeemedOfferLines(state: CartLoyaltyV2SliceType): void {
  const lineIdsToRemove = getOfferRemovalLineIds(state.loyalty);

  if (lineIdsToRemove.size) {
    state.loyalty.lines = state.loyalty.lines.filter(
      (line) => !line.lineId || !lineIdsToRemove.has(line.lineId)
    );
  }

  clearLoyaltyRedemption(state.loyalty);

  if (state.loyalty.result?.appliedOffer) {
    state.loyalty.result = {
      ...state.loyalty.result,
      appliedOffer: undefined,
    };
  }
}

const initialState: CartLoyaltyV2SliceType = {
  cart: {
    items: [],
    subTotalPrice: 0,
    deliveryFees: 0,
    vat: 0,
    service: 0,
    tips: 0,
    totalPrice: 0,
    cartItemsNumber: 0,
    redeemedOffer: undefined,
    isEligibleOffer: false,
    remainingToEligiable: 0,
    discountedValue: 0,
  },
  cartActions: [],
  redirectToItem: undefined,
  selectedOfferId: -1,
  isLoading: false,
  pendingOperations: 0,
  selectedBuySubItem: undefined,
  selectedOfferSubItems: new DoubleLinkedList<CartItemType>(),
  selectedParentOffer: undefined,
  bogoSteps: undefined,
  latestCartVersion: 0,
  pendingAppliedOfferAnalytics: undefined,
  tipUserSelection: undefined,
  loyalty: initialLoyaltyCartState,
};

const cartLoyaltyV2 = createSlice({
  name: "cartLoyaltyV2",
  initialState,
  reducers: {
    addItemAction: (state, action: PayloadAction<AddLineInput>) => {
      const normalized = normalizeAddPayload(action.payload);
      const useLoyaltyService = normalized.useLoyaltyService ?? false;

      if (useLoyaltyService) {
        state.loyalty.lines.push(buildLoyaltyOrderLine(action.payload));

        return;
      }

      let cartItem = addLineInputToCartItemType(normalized);
      cartItem = replaceNullWithUndefined(cartItem) as CartItemType;
      const cartAction: CartAction = {
        actionType: CartActionType.ADD_ITEM,
        item: cartItem,
      };

      state.cartActions.push(cartAction);
    },
    removeItemAction: (state, action: PayloadAction<RemoveLineInput>) => {
      const useLoyaltyService = action.payload.useLoyaltyService ?? false;

      if (useLoyaltyService && "lineId" in action.payload) {
        const { lineId } = action.payload;
        state.loyalty.lines = state.loyalty.lines.filter(
          (item) => item.lineId !== lineId
        );

        return;
      }

      if (useLoyaltyService && "itemIndex" in action.payload) {
        const indices = (
          Array.isArray(action.payload.itemIndex)
            ? action.payload.itemIndex
            : [action.payload.itemIndex]
        ).sort((a, b) => b - a);

        for (const index of indices) {
          state.loyalty.lines.splice(index, 1);
        }

        return;
      }

      if (!("itemIndex" in action.payload)) {
        return;
      }

      const cartAction: CartAction = {
        actionType: CartActionType.REMOVE_ITEM,
        itemIndex: action.payload.itemIndex,
      };
      state.cartActions.push(cartAction);
    },
    editItemAction: (
      state,
      action: PayloadAction<
        | ({ itemIndex: number } & AddLineInput)
        | ({ lineId: string } & AddLineInput)
      >
    ) => {
      const useLoyaltyService = action.payload.useLoyaltyService ?? false;
      const itemIndex = (action.payload as { itemIndex?: number }).itemIndex;

      if (useLoyaltyService) {
        const lineId =
          "lineId" in action.payload && action.payload.lineId
            ? action.payload.lineId
            : itemIndex !== undefined
            ? state.loyalty.lines[itemIndex]?.lineId
            : undefined;

        if (lineId) {
          const orderLine = addLineInputToOrderLine(
            normalizeAddPayload(action.payload),
            lineId
          );

          const idx = state.loyalty.lines.findIndex((i) => i.lineId === lineId);
          if (idx !== -1) {
            const existing = state.loyalty.lines[idx];
            if (!orderLine.addons?.length && existing.addons?.length) {
              orderLine.addons = existing.addons;
            }
            state.loyalty.lines[idx] = orderLine;
          }

          return;
        }
      }

      let cartItem = buildCartLine(normalizeAddPayload(action.payload), false);

      cartItem = replaceNullWithUndefined(cartItem) as CartItemType;

      if (itemIndex === undefined) {
        return;
      }

      const cartAction: CartAction = {
        actionType: CartActionType.EDIT_ITEM,
        itemIndex,
        item: cartItem,
      };
      state.cartActions.push(cartAction);
    },
    editLineQuantityAction: (
      state,
      action: PayloadAction<{
        lineId: string;
        quantity: number;
        useLoyaltyService?: boolean;
      }>
    ) => {
      const useLoyaltyService = action.payload.useLoyaltyService ?? false;

      if (!useLoyaltyService) {
        return;
      }

      const idx = state.loyalty.lines.findIndex(
        (line: OrderCartV2Line) => line.lineId === action.payload.lineId
      );

      if (idx !== -1) {
        state.loyalty.lines[idx].quantity = action.payload.quantity;
      }
    },
    setTipAction: (state, action: PayloadAction<number>) => {
      state.cart.tips = action.payload;
      // const cartAction: CartAction = {
      //   actionType: CartActionType.TIPS,
      //   amount: action.payload,
      // };
      // state.cartActions.push(cartAction);
    },
    setTipUserSelectionAction: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.tipUserSelection = action.payload;
    },
    resetCartAction: (state) => {
      state.cartActions = [];
      state.cart = initialState.cart;
      state.tipUserSelection = undefined;
      state.redirectToItem = undefined;
      state.loyalty = initialLoyaltyCartState;
    },
    redeemOfferAction: (
      state,
      action: PayloadAction<{
        offer: Offer;
        item?: CartItem[];
        franchiseId?: number;
        extraDetails?: { isFromItemFromList?: boolean };
        applyEvent?: CUSTOMER_ANALYTICS_EVENT;
        applyExtra?: AnalyticsExtraInfo;
      }>
    ) => {
      if (!(action.payload.offer instanceof Offer)) {
        return;
      }

      if (action.payload.applyEvent !== undefined) {
        state.pendingAppliedOfferAnalytics = {
          event: action.payload.applyEvent,
          extra: action.payload.applyExtra,
        };
      }

      const isFromItemFromList =
        action.payload.extraDetails?.isFromItemFromList;
      let payloadDetails = action.payload.offer.applyOffer(
        state,
        action.payload.item,
        undefined,
        { isNotFirstApply: isFromItemFromList }
      );

      if (payloadDetails) {
        // Check if the offer is a sub offer
        if (action.payload.offer.isSubOffer) {
          if (!state.selectedParentOffer || !state.selectedBuySubItem) {
            return;
          }

          // Apply Parent Offer with sub offer
          const parentOffer = createOffer(state.selectedParentOffer);
          const parentOfferPayload = parentOffer.applyOffer(
            state,
            [state.selectedBuySubItem as CartItem],
            payloadDetails
          );

          if (!parentOfferPayload) {
            return;
          }

          payloadDetails = parentOfferPayload;
          state.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();
          state.selectedParentOffer = undefined;
          state.selectedBuySubItem = undefined;
          state.bogoSteps = undefined;
        }

        const cartAction: CartAction = {
          actionType: CartActionType.REDEEM_OFFER,
          data: payloadDetails,
        };
        state.cartActions.push(cartAction);
      }
    },
    removeOfferAction: (state) => {
      const cartAction: CartAction = {
        actionType: CartActionType.REMOVE_OFFER,
      };
      state.redirectToItem = undefined;
      state.selectedOfferId = -1;
      state.cartActions.push(cartAction);
      state.selectedOfferSubItems = new DoubleLinkedList<CartItemType>();
      state.selectedBuySubItem = undefined;
      state.loyalty.applyFlow = undefined;
    },
    setRedirectItem: (
      state,
      action: PayloadAction<RedirectToItem | undefined>
    ) => {
      state.redirectToItem = action.payload;
    },
    setSelectedOfferId: (state, action: PayloadAction<number>) => {
      state.selectedOfferId = action.payload;
    },
    setSelectedBuySubItem: (
      state,
      action: PayloadAction<CartItemType | undefined>
    ) => {
      state.selectedBuySubItem = action.payload;
    },

    setSelectedOfferSubItems: (
      state,
      action: PayloadAction<DoubleLinkedList<CartItemType>>
    ) => {
      state.selectedOfferSubItems = action.payload;
    },
    nextBogoStepAction: (state) => {
      state.bogoSteps = nextBogoStep(state.bogoSteps);
    },
    previousBogoStepAction: (state) => {
      state.bogoSteps = previousBogoStep(state.bogoSteps);
    },
    receiptErrorAction: (
      state,
      action: PayloadAction<
        | {
            errorType:
              | SuccessReportTypes.ReceiptController_ConfirmReceipt_ReqItemsContainsOutDatedItems
              | SuccessReportTypes.ItemService_ValidateOutOfStockSubItemIds_ReqItemsContainsOutOfStock;
            subItemIds: number[];
            outOfStockItems: [];
          }
        | {
            errorType: SuccessReportTypes.ReceiptController_ConfirmReceipt_itemsQuantityOutOfStock;
            outOfStockItems: OutOfStockItem[];
            subItemIds: [];
          }
      >
    ) => {
      const cartAction: CartAction = {
        actionType: CartActionType.CREATE_RECEIPT_ERROR,
        error: {
          errorType: action.payload.errorType,
          subItemIds: action.payload.subItemIds,
          outOfStockItems: action.payload.outOfStockItems,
        },
      };
      state.cartActions.push(cartAction);
    },
    resetLoading: (state) => {
      state.isLoading = false;
    },
    setCustomerLoyaltyOffers: (
      state,
      action: PayloadAction<LoyaltyOfferType[] | undefined>
    ) => {
      state.loyalty.customerOffers = action.payload;
    },
    redeemLoyaltyOffer: (state, action: PayloadAction<LoyaltyOfferType>) => {
      clearRedeemedOfferLines(state);
      state.loyalty.payWithPoints = undefined;

      state.loyalty.redemption = {
        offer: action.payload,
        offerApplicationId: uuidv4(),
      };
    },
    removeLoyaltyOffer: (state) => {
      clearRedeemedOfferLines(state);
      if ((state.pendingOperations ?? 0) === 0) {
        state.isLoading = false;
      }
    },
    applyPayWithPoints: (
      state,
      action: PayloadAction<{ points: number; useMaxPoints?: boolean }>
    ) => {
      clearRedeemedOfferLines(state);

      state.loyalty.payWithPoints = {
        requestedPoints: action.payload.points,
        ...(action.payload.useMaxPoints && { useMaxPoints: true }),
      };
    },
    removePayWithPoints: (state) => {
      state.loyalty.payWithPoints = undefined;
      if ((state.pendingOperations ?? 0) === 0) {
        state.isLoading = false;
      }
    },
    setLoyaltyOfferError: (
      state,
      action: PayloadAction<{ code?: string; message?: string }>
    ) => {
      state.loyalty.offerError = action.payload;
    },
    clearLoyaltyOfferError: (state) => {
      state.loyalty.offerError = undefined;
    },
    setSelectedLoyaltyOfferId: (
      state,
      action: PayloadAction<string | undefined>
    ) => {
      if (action.payload === undefined) {
        if (state.loyalty.applyFlow) {
          state.loyalty.applyFlow.selectedOfferId = undefined;
        }

        return;
      }

      state.loyalty.applyFlow = {
        ...state.loyalty.applyFlow,
        selectedOfferId: action.payload,
      };
    },
    clearLoyaltyApplyFlow: (state) => {
      state.loyalty.applyFlow = undefined;
    },
    /** Opens the offer page; seeded selections reopen an existing draft. */
    startLoyaltyOfferBuilder: (
      state,
      action: PayloadAction<{
        offerId: string;
        buySelections?: Record<string, number>;
        rewardSelections?: Record<string, number>;
        customisations?: Record<string, LoyaltyOfferBuilderCustomisation>;
        note?: string;
      }>
    ) => {
      const { offerId, buySelections, rewardSelections, customisations, note } =
        action.payload;

      // A remount or step change must not wipe what the guest picked.
      if (
        state.loyalty.offerBuilder?.offerId === offerId &&
        !buySelections &&
        !rewardSelections
      ) {
        return;
      }

      state.loyalty.offerBuilder = {
        offerId,
        buySelections: buySelections ?? {},
        rewardSelections: rewardSelections ?? {},
        customisations: customisations ?? {},
        note: note ?? "",
      };
    },
    /** `offerSelection` already clamped the map, so this is a plain assign. */
    setLoyaltyOfferBuilderSelections: (
      state,
      action: PayloadAction<{
        side: "buy" | "reward";
        selections: Record<string, number>;
      }>
    ) => {
      const builder = state.loyalty.offerBuilder;
      if (!builder) {
        return;
      }

      if (action.payload.side === "buy") {
        builder.buySelections = action.payload.selections;
      } else {
        builder.rewardSelections = action.payload.selections;
      }

      // Drop customisations whose entity is no longer selected.
      const liveKeys = new Set(
        Object.keys(builder.buySelections).concat(
          Object.keys(builder.rewardSelections)
        )
      );
      for (const key of Object.keys(builder.customisations)) {
        const entityId = key.split(":")[1];
        if (!liveKeys.has(entityId)) {
          delete builder.customisations[key];
        }
      }
    },
    setLoyaltyOfferBuilderCustomisation: (
      state,
      action: PayloadAction<{
        key: string;
        customisation: LoyaltyOfferBuilderCustomisation;
      }>
    ) => {
      const builder = state.loyalty.offerBuilder;
      if (builder) {
        builder.customisations[action.payload.key] =
          action.payload.customisation;
      }
    },
    setLoyaltyOfferBuilderNote: (state, action: PayloadAction<string>) => {
      if (state.loyalty.offerBuilder) {
        state.loyalty.offerBuilder.note = action.payload;
      }
    },
    clearLoyaltyOfferBuilder: (state) => {
      state.loyalty.offerBuilder = undefined;
    },
    /**
     * Pushes the chosen lines and records which are triggers vs selections for
     * the next calculate (see `utils/cart/appliedOffer`).
     */
    commitLoyaltyOfferBuilder: (
      state,
      action: PayloadAction<{
        offer: LoyaltyOfferType;
        buyLines: AddLineInput[];
        rewardLines: AddLineInput[];
      }>
    ) => {
      // Opens the redemption here rather than expecting a prior
      // `redeemLoyaltyOffer`, so lines and selections land in one action and
      // the middleware fires a single calculate that already has them.
      clearRedeemedOfferLines(state);
      state.loyalty.payWithPoints = undefined;

      state.loyalty.redemption = {
        offer: action.payload.offer,
        offerApplicationId: uuidv4(),
      };
      const redemption = state.loyalty.redemption;

      const pushed: {
        buy: OrderCartV2OfferSelection[];
        reward: OrderCartV2OfferSelection[];
      } = { buy: [], reward: [] };

      const pushSide = (
        lines: AddLineInput[],
        side: "buy" | "reward"
      ): void => {
        for (const line of lines) {
          const lineId = line.lineId ?? uuidv4();
          state.loyalty.lines.push(buildLoyaltyOrderLine({ ...line, lineId }));
          pushed[side].push({ lineId, quantity: line.count ?? 1 });
        }
      };

      pushSide(action.payload.buyLines, "buy");
      pushSide(action.payload.rewardLines, "reward");

      const addedLineIds = [...pushed.buy, ...pushed.reward].map(
        (selection) => selection.lineId
      );

      redemption.addedLineIds = addedLineIds;
      redemption.pendingSelections = pushed.reward;
      redemption.pendingTriggers = pushed.buy.length ? pushed.buy : undefined;

      state.loyalty.offerBuilder = undefined;
      state.loyalty.applyFlow = undefined;
    },
    // Runs LoyaltyOffer.applyOffer() inside a reducer so it can mutate state
    // (set redirectToItem / selectedLoyaltyOfferId) and optionally add to cart.
    applyLoyaltyItemOffer: (
      state,
      action: PayloadAction<{
        offer: LoyaltyOfferType;
        menuSubItems: SubItemType[];
        cartItems?: CartItemType[];
        customisedLines?: AddLineInput[];
        cartVersion: number;
        useLoyaltyService?: boolean;
      }>
    ) => {
      const mutableSubItems: SubItemType[] = JSON.parse(
        JSON.stringify(action.payload.menuSubItems)
      );
      const useLoyaltyService = action.payload.useLoyaltyService ?? false;

      if (!useLoyaltyService) {
        return;
      }
      const offer = new LoyaltyItemOffer(action.payload.offer);
      const addInput = offer.applyOffer(
        state,
        mutableSubItems,
        action.payload.customisedLines
      );

      if (addInput) {
        const cartVersionMatches =
          action.payload.cartVersion === state.latestCartVersion;
        const isCompletingFreshRedemption =
          !!state.loyalty.redemption &&
          !(state.loyalty.redemption.addedLineIds?.length ?? 0);

        if (cartVersionMatches || isCompletingFreshRedemption) {
          const lineId = addInput.lineId ?? uuidv4();
          state.loyalty.lines.push(
            buildLoyaltyOrderLine({ ...addInput, lineId })
          );
          const redemption = state.loyalty.redemption;
          if (redemption) {
            const quantity = addInput.count ?? 1;
            redemption.addedLineIds = [
              ...(redemption.addedLineIds ?? []),
              lineId,
            ];
            redemption.pendingSelections = [
              ...(redemption.pendingSelections ?? []),
              { lineId, quantity },
            ];
          }
        }
      } else if (
        !addInput &&
        (state.pendingOperations ?? 0) === 0 &&
        state.loyalty.applyFlow?.redirect
      ) {
        // Redirect-only apply (item-from-list / addon item page): calculate is
        // skipped, so do not leave a stale loading flag from older applyOffer code.
        state.isLoading = false;
      }

      // const loyaltyOffer = createLoyaltyOffer(action.payload.offer);
      // if (!(loyaltyOffer instanceof LoyaltyItemOffer)) {
      //   return;
      // }

      // const cartItemResult = loyaltyOffer.applyLegacyOffer(
      //   state,
      //   mutableSubItems,
      //   action.payload.cartItems
      // );

      // if (
      //   cartItemResult &&
      //   action.payload.cartVersion === state.latestCartVersion
      // ) {
      //   const lineId = cartItemResult.lineId ?? uuidv4();
      //   const item = {
      //     ...cartItemResult,
      //     lineId,
      //   };
      //   state.cartActions.push({ actionType: CartActionType.ADD_ITEM, item });
      // }
    },
    incrementLatestCartVersion: (state) => {
      state.latestCartVersion += 1;
    },
    setPendingAppliedOfferAnalyticsEvent: (
      state,
      action: PayloadAction<
        | { event: CUSTOMER_ANALYTICS_EVENT; extra?: AnalyticsExtraInfo }
        | undefined
      >
    ) => {
      state.pendingAppliedOfferAnalytics = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(calculateCartWithOffersCall.pending, (state) => {
      state.isLoading = true;
      state.pendingOperations = (state.pendingOperations || 0) + 1;
    });

    builder.addCase(
      calculateCartWithOffersCall.fulfilled,
      (state, { payload }) => {
        if (payload) {
          state.cart = { ...payload, tips: state.cart.tips };
        }
        state.pendingOperations = Math.max(
          0,
          (state.pendingOperations || 1) - 1
        );

        state.isLoading = state.pendingOperations > 0;
      }
    );

    builder.addCase(
      calculateCartWithOffersCall.rejected,
      (state, { error, meta }) => {
        state.pendingOperations = Math.max(
          0,
          (state.pendingOperations || 1) - 1
        );

        state.isLoading = state.pendingOperations > 0;
        const errMsg = JSON.stringify(error.message);
        const currentRetryCount = meta?.arg?.retryCount || 0;
        const isNetworkError = isNetworkErroFunc(errMsg);

        // For non-network errors on first try, pop the last action
        // The middleware will handle retrying with the remaining actions
        if (!isNetworkError && currentRetryCount < DEFAULT_RETRY) {
          state.cartActions.pop();
        }

        // Reset the cart if we've reached max retries
        // The middleware won't retry anymore in this case
        if (
          (isNetworkError && currentRetryCount >= NETWORK_ERROR_RETRY) ||
          (!isNetworkError && currentRetryCount >= DEFAULT_RETRY) ||
          state.cartActions.length === 0
        ) {
          // Reset cart state
          state.cartActions = [];
          state.cart = initialState.cart;
          state.loyalty.lines = [];
        }
      }
    );

    // Loyalty v2 calculate handlers — mirrors the existing pattern above
    builder.addCase(calculateOrderCartV2Call.pending, (state) => {
      state.isLoading = true;
      state.pendingOperations = (state.pendingOperations || 0) + 1;
    });

    builder.addCase(
      calculateOrderCartV2Call.fulfilled,
      (state, { payload, meta }) => {
        state.pendingOperations = Math.max(
          0,
          (state.pendingOperations || 1) - 1
        );
        state.isLoading = state.pendingOperations > 0;

        if (meta.arg.cartVersion !== state.latestCartVersion) {
          return;
        }
        if (payload) {
          state.loyalty.lines = applyCartOfferResponseStatus(
            state.loyalty,
            payload.loyaltyCartResult,
            payload.orderLines
          );
          state.loyalty.result = {
            ...payload.loyaltyCartResult,
            cartVersion: meta.arg.cartVersion,
          };

          const pricing = payload.loyaltyCartResult.pricing;
          state.cart.subTotalPrice = pricing.subtotal;
          state.cart.totalPrice = pricing.total;
          state.cart.vat = pricing.tax;
          state.cart.discountedValue = pricing.discounts?.total ?? 0;
          state.cart.service = pricing.serviceCharge ?? 0;
          state.cart.deliveryFees = pricing.delivery?.total ?? 0;
        }
      }
    );

    builder.addCase(
      calculateOrderCartV2Call.rejected,
      (state, { error, meta }) => {
        state.pendingOperations = Math.max(
          0,
          (state.pendingOperations || 1) - 1
        );
        state.isLoading = state.pendingOperations > 0;

        // Aborted requests (from AbortController) are silent — no retry, no reset.
        // Axios 0.x uses message="canceled"; Axios 1.x adds code="ERR_CANCELED".
        if (error.code === "ERR_CANCELED" || error.message === "canceled") {
          return;
        }

        const errMsg = JSON.stringify(error.message);
        const currentRetryCount = meta?.arg?.retryCount || 0;
        const isNetworkError = isNetworkErroFunc(errMsg);

        if (!isNetworkError && currentRetryCount < DEFAULT_RETRY) {
          state.loyalty.lines.pop();
        }

        if (
          (isNetworkError && currentRetryCount >= NETWORK_ERROR_RETRY) ||
          (!isNetworkError && currentRetryCount >= DEFAULT_RETRY) ||
          state.loyalty.lines.length === 0
        ) {
          state.cart = initialState.cart;
          state.loyalty.result = undefined;
          state.loyalty.lines = [];
        }
      }
    );
  },
});

export const {
  addItemAction,
  removeItemAction,
  editItemAction,
  editLineQuantityAction,
  setTipAction,
  setTipUserSelectionAction,
  resetCartAction,
  redeemOfferAction,
  removeOfferAction,
  setRedirectItem,
  setSelectedOfferId,
  receiptErrorAction,
  setSelectedBuySubItem,
  setSelectedOfferSubItems,
  resetLoading,
  nextBogoStepAction,
  previousBogoStepAction,
  setCustomerLoyaltyOffers,
  redeemLoyaltyOffer,
  removeLoyaltyOffer,
  applyPayWithPoints,
  removePayWithPoints,
  setLoyaltyOfferError,
  clearLoyaltyOfferError,
  setSelectedLoyaltyOfferId,
  clearLoyaltyApplyFlow,
  applyLoyaltyItemOffer,
  incrementLatestCartVersion,
  setPendingAppliedOfferAnalyticsEvent,
  startLoyaltyOfferBuilder,
  setLoyaltyOfferBuilderSelections,
  setLoyaltyOfferBuilderCustomisation,
  setLoyaltyOfferBuilderNote,
  clearLoyaltyOfferBuilder,
  commitLoyaltyOfferBuilder,
} = cartLoyaltyV2.actions;

export default cartLoyaltyV2.reducer;
